import React, { useState } from 'react';
import { MessageBubble, MessageText } from '@engyalo/design-system';
import { useTheme } from '@mui/material/styles';
import i18n from 'i18n';
import { formatMessageTime } from 'helpers/formatterDates';
import { useTranslation } from 'react-i18next';
import DropDown from '../../../DropDown';
import InteractiveMessage from '../InteractiveMessage';
import { MediaTypesView } from '../MediaTypes/MediaTypesView';
import { MessageHeaderUserInfo } from '../MessageHeaderUserInfo/MessageHeaderUserInfo';
import { ChatBubble, ContentItemMessage, ListItemMessage } from '../style';
import { IMessageProps } from '../type';
import { createKeyToken } from '../../../../helpers/createKeyToken';
import ChatBubbleTail, { TailDirection } from '../ChatBubble/ChatBubbleTail';

const CLICK_DURATION_MOBILE = 1000;

const Message: React.FC<IMessageProps> = (props) => {
  const theme = useTheme();
  const {
    isbot,
    isclient,
    isUserlogged,
    type,
    itemMsg,
    currentDate,
    hasError,
    isAnotation,
    hasAttachments,
    messageErrorOptions,
    messageMediaOptions,
    messageTextOptions,
    username,
    hasCustomFields,
    messages,
    handleMediaDownload,
  } = props;

  const [onTouchStartValue, setOnTouchStartValue] = useState(0);
  const [isLongClick, setIsLongClick] = useState(false);
  const { t } = useTranslation();
  const CHATBUBBLEID = 'chatBubble';
  const tailDirection = isbot || isUserlogged ? TailDirection.RIGHT : TailDirection.LEFT;

  const linkify = (txt: string) => {
    const urlRegex = /((https|ftp|http)?:\/\/[^\s]+)/g;
    return txt.split(' ').map((part) =>
      urlRegex.test(part) ? (
        <a
          key={createKeyToken()}
          href={part.includes('http') ? part : `https://${part}`}
          style={{ textDecoration: 'underline' }}
          rel="noopener noreferrer"
          target="_blank"
        >
          {part}{' '}
        </a>
      ) : (
        `${part} `
      )
    );
  };

  const ISDISCUSSION = type === 'discussion';

  const checkListOptions = () => {
    if (hasError) {
      return messageErrorOptions;
    }

    if (hasAttachments) {
      return messageMediaOptions;
    }

    return messageTextOptions;
  };

  let sentAt;
  try {
    sentAt = formatMessageTime(currentDate.toString(), i18n.language);
  } catch {
    sentAt = '';
  }

  return (
    <ListItemMessage
      isBot={isbot}
      isClient={isclient}
      isUserLogged={isUserlogged}
      type={type}
      data-testid="messageItem"
    >
      <ContentItemMessage
        data-testid="contentItemMessage"
        onTouchStart={(event) => setOnTouchStartValue(event.timeStamp)}
        onTouchEnd={(event) => {
          const isLongClickCalc = event.timeStamp - onTouchStartValue;
          if (isLongClickCalc > CLICK_DURATION_MOBILE) {
            setOnTouchStartValue(0);
            setIsLongClick(true);
          }
        }}
        type={type}
      >
        {hasAttachments ? (
          <ChatBubble
            isUserLogged={isUserlogged}
            isBot={isbot}
            isError={hasError}
            isMedia={hasAttachments}
            isAnotation={isAnotation}
            isDiscussion={ISDISCUSSION}
            id={CHATBUBBLEID}
          >
            {!isAnotation && (
              <DropDown
                msg={itemMsg.msg}
                handleIsLongClick={() => {
                  setIsLongClick(false);
                }}
                isLongClick={isLongClick}
                isUserlogged={isUserlogged}
                isbot={isbot}
                iserror={hasError}
                ismidia={hasAttachments}
                isanotation={isAnotation}
                isdiscussion={ISDISCUSSION}
                colorIcon={isclient ? theme.palette.neutral.var50 : theme.palette.common.white}
                downloadMedia={() =>
                  itemMsg.attachments && handleMediaDownload(itemMsg?.attachments[0], itemMsg.username)
                }
                item={CHATBUBBLEID}
                listOptions={checkListOptions()}
                position="absolute"
              />
            )}
            <MediaTypesView username={username} attachments={itemMsg.attachments!} type={type} />
            <ChatBubbleTail visible={!isAnotation && !ISDISCUSSION} direction={tailDirection} />
          </ChatBubble>
        ) : (
          <MessageBubble
            style={{ marginTop: 0 }}
            data-testid="text-message"
            variant={hasError ? 'error' : 'internal'}
            displayName={t('discussionBubbleDisplayName', { agent: username })}
            sentAt={sentAt}
          >
            <MessageText body={linkify(itemMsg.msg)} />
          </MessageBubble>
        )}

        {hasCustomFields && (
          <InteractiveMessage messageId={itemMsg.id} customFields={itemMsg.customFields} messages={messages} />
        )}

        <MessageHeaderUserInfo
          isUserlogged={isUserlogged}
          itemMsg={itemMsg}
          currentDate={currentDate}
          errorSendMessage={hasError}
          isbot={isbot}
          isAnotation={isAnotation}
        />
      </ContentItemMessage>
    </ListItemMessage>
  );
};

export default Message;
